/**
 * The blurred desktop preview.
 *
 * The agent pushes a small JPEG every second or so while this page is watching the device. Each one
 * has to be decoded and uploaded to the GPU, and **the texture it becomes stays resident until
 * something destroys it** - which is the whole reason this is a class rather than four lines in the
 * shell. A frame handler that only assigned `image.source` would leak one texture per frame, and a
 * technician who left the page open over lunch would run the tab out of GPU memory.
 *
 * So: one decode at a time (a frame arriving mid-decode replaces the pending one and the older is
 * dropped, the rule the remote console's canvas renderer already follows), the previous texture
 * destroyed as the new one is assigned, and `dispose()` destroying the last one.
 *
 * It is blurred on the AGENT, not here - the bytes that arrive are already unreadable, which is what
 * makes this safe to show to anyone who can open the device page. Take control is the surface that
 * shows real pixels, and it authorizes separately.
 */
import { ImageControl, insets, type Desktop, type WebwinImage } from 'webwin';
import type { DesktopThumbnail } from '../../contracts.gen.js';
import { RemoteDesktopKind } from '../../contracts.gen.js';
import { CrmLabel } from '../../ui/Text.js';
import { card, FONT, GAP, stackOf, vstack } from '../../ui/layout.js';
import type { Card } from '../../ui/Card.js';

/** The widths the XAML used: a 280px picture in a dark well. */
const WIDTH = 280;

/** The well's outer width: the picture plus its padding. What sits under it is held to this. */
export const PREVIEW_COLUMN = WIDTH + 2 * GAP.md;

export class DevicePreview {
  /**
   * The dark well to add to the page.
   *
   * Present from the moment the page opens, with the caption saying it is waiting: an empty well is
   * how a technician learns the preview exists at all, and one that appeared only once a frame
   * arrived would read as the feature being absent on every machine that is asleep.
   */
  readonly panel: Card;

  readonly #desktop: Desktop;
  readonly #image: ImageControl;
  readonly #caption: CrmLabel;
  #current: WebwinImage | null = null;
  #decoding = false;
  #pending: DesktopThumbnail | null = null;
  #disposed = false;

  constructor(desktop: Desktop) {
    this.#desktop = desktop;

    this.#image = new ImageControl({
      width: WIDTH,
      // 16:10 until a frame tells us otherwise; `fit` letterboxes whatever really arrives, so an
      // ultrawide desktop is not stretched.
      height: Math.round((WIDTH * 10) / 16),
      sizeMode: 'fit',
      showBorder: false,
      visible: false
    });

    // The well is the navigation rail's colour in both themes, so the letterbox around a captured
    // desktop reads as chrome rather than as part of the picture.
    this.#caption = new CrmLabel({
      text: 'Waiting for preview…',
      font: FONT.tiny,
      role: 'sidebarMuted',
      hAlign: 'center',
      autoWidth: true,
      autoHeight: true
    });

    // `autoWidth` because the well sits in a row beside a paragraph: without it a card keeps the
    // 200px a Panel is born with and the 280px picture is clipped inside it.
    this.panel = card({
      tint: 'sidebar',
      bordered: false,
      padding: insets(GAP.md),
      autoWidth: true,
      hAlign: 'start',
      vAlign: 'start'
    });
    this.panel.controls.add(
      stackOf(vstack({ spacing: GAP.sm, autoWidth: true }), this.#image, this.#caption)
    );
  }

  /**
   * Takes a frame.
   *
   * Never awaited by the caller: a hub handler that awaited a decode would hold the connection's
   * dispatch while the GPU worked.
   */
  push(frame: DesktopThumbnail): void {
    if (this.#disposed) return;
    this.#caption.text = captionFor(frame.desktop);
    if (this.#decoding) {
      // Newest wins. An older frame is worth nothing once a newer one exists, and queueing them
      // would make a slow decode fall further behind every second - "frozen", not "slow".
      this.#pending = frame;
      return;
    }
    void this.#decode(frame);
  }

  async #decode(frame: DesktopThumbnail): Promise<void> {
    this.#decoding = true;
    try {
      const bitmap = await createImageBitmap(jpegBlob(frame.jpeg));
      if (this.#disposed) {
        bitmap.close();
        return;
      }
      // The bitmap IS the texture's image: it is uploaded when the texture is first drawn, not here,
      // and the texture's destroy() closes it. Closing it now leaves the GPU nothing to copy.
      const next = this.#desktop.images.create(bitmap, `device-preview-${frame.deviceId}`);
      // Assign first, then destroy: the control must never hold a destroyed texture, not even for
      // the width of a statement.
      const previous = this.#current;
      this.#current = next;
      this.#image.source = next;
      this.#image.height = Math.max(1, Math.round((WIDTH * frame.height) / Math.max(1, frame.width)));
      this.#image.visible = true;
      previous?.destroy();
    } catch {
      // A malformed frame is dropped and the last good picture stays. Blanking the preview on one
      // bad decode would read as the device having gone away.
    } finally {
      this.#decoding = false;
      const next = this.#pending;
      this.#pending = null;
      if (next && !this.#disposed) void this.#decode(next);
    }
  }

  /** The device went offline: the picture is no longer of anything. */
  clear(): void {
    this.#pending = null;
    this.#image.visible = false;
    this.#image.source = null;
    this.#current?.destroy();
    this.#current = null;
    this.#caption.text = 'Waiting for preview…';
  }

  dispose(): void {
    this.#disposed = true;
    this.#pending = null;
    this.#image.source = null;
    this.#current?.destroy();
    this.#current = null;
  }
}

/**
 * Says which desktop is being shown.
 *
 * Load-bearing rather than decorative: the login screen and a UAC prompt live on desktops of their
 * own, and a technician who cannot tell "the machine is at the lock screen" from "the preview has
 * stopped updating" will go looking for a fault in the agent.
 */
function captionFor(kind: RemoteDesktopKind): string {
  switch (kind) {
    case RemoteDesktopKind.Winlogon:
      return 'Login / lock screen';
    case RemoteDesktopKind.Secure:
      return 'Secure desktop (UAC prompt)';
    case RemoteDesktopKind.Default:
      return 'Desktop preview (blurred)';
    default:
      return 'Waiting for preview…';
  }
}

/** The hub sends the JPEG as base64, which is what System.Text.Json does with a `byte[]`. */
function jpegBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'image/jpeg' });
}
